import React from 'react';
import styled, { x } from '@xstyled/styled-components';
import { Flex } from '~/ui/flex/Flex';
import { Item, registry, RenderProps } from '../Item';
import { TEXT_MESSAGES } from '../itemIds';
import imageFooter from './iMessage.png';
import llamaImage from './llama.svg';
import { MessageProvider, useMessageStore } from './MessageStore';
import tail from './tail.svg';
import { isMobile } from 'react-device-detect';
import { t } from 'ttag';
import { TrainingState, useTrainingStore } from '~/training/state';
import { select } from '~/lib/select';
import { Logo } from '~/ui/logo/Logo';
import { useEditorStore } from '~/shell/Store';
import { VisuallyHidden } from '~/lib/a11y/VisuallyHidden';

const Llama = styled(llamaImage)`
  width: 52px;
  height: 52px;
  margin-left: auto;
  margin-right: auto;
`;

const Header = styled.divBox`
  height: 100px;
  background: ${p => p.theme.colors.greyLight};
  border-top-left-radius: 20px;
  border-top-right-radius: 20px;
  text-align: center;
  padding-top: 15px;
  > p {
    padding-top: 8px;
    line-height: 15px;
    font-size: 12px;
    align-items: center;
    color: ${p => p.theme.colors.black};
  }
`;

const Phone = styled.divBox`
  display: flex;
  flex-direction: column;
  /* CAT-122x / WCAG 1.4.10: 350px design, but never force H-scroll at 320 CSS px */
  width: 100%;
  max-width: 350px;
  box-sizing: border-box;
  min-height: 650px;
  background: ${p => p.theme.colors.white};
  box-shadow:
    0px -2px 4px rgba(0, 0, 0, 0.05),
    0px 30px 30px rgba(0, 0, 0, 0.03),
    0px 15px 20px rgba(0, 0, 0, 0.05);
  border-radius: 20px;
`;

const Chat = styled.divBox`
  padding: 0;
  flex-grow: 1;
  margin: 25px;
  height: 100%;
`;

/** Decorative iMessage input chrome (294px asset). Scale to the phone card
 *  so it never overflows at ≤320 CSS px (WCAG 1.4.10 / CAT-1303). */
const FooterImage = styled.img`
  display: block;
  max-width: calc(100% - 24px);
  width: auto;
  height: auto;
  margin: 0 auto 20px;
  box-sizing: border-box;
`;

type Variants = 'default' | 'llama';

interface Props {
  value: string;
  variant?: Variants;
  weNeedToUseOwnFeedback?: boolean;
}

/** Same positioning as Message.tsx Tail so the bubble tip sits flush. */
const Tail = styled(tail)`
  width: 22px;
  height: 23px;
  position: relative;
  margin-left: -15px;
  bottom: -15px;
  margin-top: -23px;
  fill: ${p => p.theme.colors.greyMed};
`;

const TextMessage = styled.div`
  display: table;
  background-color: ${p => p.theme.colors.greyMed};
  border-radius: 20px;
  margin-top: 15px;
  padding: 10px;
`;

/**
 * CAT-1300 / WCAG 4.1.3: live region exists empty on first paint, then receives
 * answer feedback (e.g. "That's right!"). Mounting aria-live together with the
 * message often skips screen-reader announcements. Visual bubble is decorative
 * for AT (aria-hidden) so the status is announced once.
 */
const Feedback = () => {
  const { feedback } = useMessageStore();

  return (
    <>
      <VisuallyHidden role="status" aria-live="polite" aria-atomic="true">
        {feedback}
      </VisuallyHidden>
      {feedback ? (
        <TextMessage aria-hidden="true">
          {feedback}
          <Tail aria-hidden={true} />
        </TextMessage>
      ) : null}
    </>
  );
};

const selector = (s: TrainingState) => select(s, 'companyName', 'logoUrl');

export class TextMessages extends Item<typeof TEXT_MESSAGES, Props> {
  protected render({
    children,
    variant,
    weNeedToUseOwnFeedback = false
  }: RenderProps<Props>) {
    const { editorMode } = useEditorStore();
    const { logoUrl, companyName } = useTrainingStore(selector);

    return (
      <MessageProvider>
        <Flex variant="center" w="100%" minWidth={0}>
          <Phone className="text-messages">
            <Header className="text-messages-header">
              {variant == 'llama' && (
                <>
                  {logoUrl ? <Logo src={logoUrl} height={52} /> : <Llama />}
                  <p>{companyName || 'EasyLlama'}</p>
                </>
              )}
            </Header>
            <Chat className="text-messages-chat">
              <x.span position={'absolute'} visibility={'hidden'}>
                {'EasyLlama ' + t`says`}
              </x.span>
              {children}
              {weNeedToUseOwnFeedback && <Feedback />}
            </Chat>
            {!isMobile && !editorMode && (
              <FooterImage
                className="invertStyleForDarkMode text-messages-footer"
                src={imageFooter}
                aria-hidden={true}
                alt=""
              />
            )}
          </Phone>
        </Flex>
      </MessageProvider>
    );
  }
}

registry[TEXT_MESSAGES] = TextMessages;
